// Login.jsx
import React, { useContext, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import "./login.css";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../context/context";
import Cookies from "js-cookie";

const Login = () => {
  const { setUser, setLogin } = useContext(MyContext);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  const checkStudentRegistration = async (userEmail, profile) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/auth/check-student`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: userEmail }),
        }
      );

      const data = await response.json();

      if (response.ok && data.exists) {
        // Student is registered, proceed with login
        const userData = {
          profileImage: profile,
          ...data,
        };
        Cookies.set("token", JSON.stringify(data.token), { expires: 7 });

        localStorage.setItem("student", JSON.stringify(userData));
        setUser(userData);
        setLogin(true);
        setTimeout(() => navigate("/course", { replace: true }), 0);

        return true;
      } else {
        // Student not registered
        setError(
          "You are not registered in any program. Please contact administration."
        );
        return false;
      }
    } catch (error) {
      console.error("Error checking student registration:", error);
      setError("Error checking registration. Please try again.");
      return false;
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setError("");

    try {
      // Decode the JWT token to get user info
      const decoded = jwtDecode(credentialResponse.credential);
      const userEmail = decoded.email;
      const profilePic = decoded.picture;

      // Check if this email exists in students collection
      const isRegistered = await checkStudentRegistration(
        userEmail,
        profilePic
      );

      if (!isRegistered) {
        // If not registered, we don't proceed with login
        return;
      }
    } catch (error) {
      console.error("Google login error:", error);
      setError("Google login failed. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google login failed. Please try again.");
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleDemoLogin = () => {
    setEmail("demo@student.com");
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="background-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      <div className="login-card">
        {/* Header Section */}
        <div className="login-header">
          <div className="logo">
            <div className="logo-icon">🚀</div>
            <div className="logo-text">
              <span className="logo-primary">Zavrex</span>
              <span className="logo-secondary">IBIX Qodify Technologies</span>
            </div>
          </div>
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">
            Sign in to continue your learning journey
          </p>
        </div>

        {/* Google Login Button */}
        <div className="google-login-section">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="filled_blue"
            size="large"
            text="continue_with"
            shape="rectangular"
            width="100%"
          />
          {googleLoading && (
            <div className="google-loading">
              <span className="button-spinner"></span>
              Checking registration...
            </div>
          )}
        </div>
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="login-footer">
          <p className="footer-text">
            New to IQT?{" "}
            <a
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn"
            >
              <i className="fab fa-whatsapp"></i>
              Chat with us
            </a>
          </p>
        </div>
      </div>

      {/* Features Showcase */}
      <div className="features-sidebar">
        <div className="features-content">
          <h3>Learn. Grow. Succeed in IT with IBIX.</h3>
          <div className="feature-list">
            <div className="feature-item">
              <span className="feature-icon">🎓</span>
              <div className="feature-text">
                <strong>Interactive Classes</strong>
                <span>Learn with hands-on examples</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💻</span>
              <div className="feature-text">
                <strong>Coding Practices</strong>
                <span>Real-world projects & exercises</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📊</span>
              <div className="feature-text">
                <strong>Progress Tracking</strong>
                <span>Monitor your learning journey</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🏆</span>
              <div className="feature-text">
                <strong>Achievements</strong>
                <span>Earn badges & certificates</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
